// 3D touch-drop animation (PAYLOAD_PLACE), driven by telemetry, not a clock.
//
//   carry    parcel held in the gripper under the drone's belly (no rope)
//   released gripper open, parcel left on the ground, drone climbs away empty
//   idle     every parcel delivered
//
// When is the parcel down? ArduCopter's PAYLOAD_PLACE descends from the item's
// start height at a steady rate until it senses the ground, releases, and climbs
// straight back — it sits on the ground only ~0.5 s (SITL 2026-11-04: "SIM Hit
// ground", "payload release thrust threshold", "Mission: 7 WP" within a second).
// So the release is the END OF THAT DESCENT: near the drop, more than
// BELOW_START_M under the item's start height (that excludes the hover at the
// start height itself), having been descending, and now no longer descending.
// No duration is required — a hold of "still for N ms" missed a 0.5 s touchdown
// sampled at 4 Hz. No fixed height band either: telemetry altitude is
// home-relative, and a stop on higher ground touches down metres "above" home.
//
// Without a climb rate in the telemetry: the height stays within FLAT_M for
// FLAT_MS while that far below the start height.
//
// One step per frame. Returns the next state and the drop key let go on this step.

export const BELOW_START_M = 2;     // must be this far under the PAYLOAD_PLACE start height
export const DESCENDING_MPS = -0.15; // climb rate that counts as descending
export const STOPPED_MPS = -0.05;   // ...and as no longer descending
const FLAT_M = 0.1;                 // no climb rate: height change that counts as moving
const FLAT_MS = 400;                // ...and how long it must stay inside that
const CLEAR_ABOVE_M = 2;            // climbed this far before the next parcel shows

/**
 * @param {object} st  previous state ({ phase: 'carry' } to start)
 * @param {{agl:number, climbRate:?number, nowMs:number, remaining:number,
 *          nearDrop:?{key:string, startAlt:number}}} input
 *        agl        home-relative height, m (telemetry relative altitude)
 *        climbRate  m/s, positive up; null/undefined when the telemetry has none
 *        remaining  parcels not yet delivered (before this step)
 *        nearDrop   the undelivered drop within range, with its PAYLOAD_PLACE
 *                   start height (home-relative), or null
 * @returns {object} next state, with `release` = the drop key let go on this step (or null)
 */
export function stepTouchDrop(st, { agl, climbRate, nowMs, remaining, nearDrop }) {
  const next = { ...st, release: null };
  const known = Number.isFinite(agl);

  if (st.phase === 'carry') {
    if (remaining === 0) { next.phase = 'idle'; return next; }
    const startAlt = nearDrop && Number.isFinite(nearDrop.startAlt) ? nearDrop.startAlt : null;
    const placing = !!nearDrop && known && startAlt !== null && agl < startAlt - BELOW_START_M;
    if (!placing) {
      Object.assign(next, { descending: false, flatAgl: null, flatSince: null });
      return next;
    }

    let landed;
    if (Number.isFinite(climbRate)) {
      if (climbRate <= DESCENDING_MPS) next.descending = true;
      landed = next.descending && climbRate >= STOPPED_MPS;
    } else {
      if (!Number.isFinite(st.flatAgl) || Math.abs(agl - st.flatAgl) > FLAT_M) {
        next.flatAgl = agl;
        next.flatSince = nowMs;
      }
      landed = nowMs - next.flatSince >= FLAT_MS;
    }
    if (landed) {
      Object.assign(next, { phase: 'released', release: nearDrop.key, releaseAgl: agl, descending: false, flatAgl: null, flatSince: null });
    }
  } else if (st.phase === 'released' && known && agl > (st.releaseAgl || 0) + CLEAR_ABOVE_M) {
    next.phase = remaining > 0 ? 'carry' : 'idle';
  }
  return next;
}
